import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useVehicles } from '../../../hooks/useVehicles';
import VehicleCard from '../../inventory/components/VehicleCard';
import NearbyAreasSection from '../components/NearbyAreasSection';
import ReadyToShopCta from '../components/ReadyToShopCta';
import { injectJsonLd, removeJsonLd } from '../../../lib/schemaBuilder';
import { useSEOHead } from '../../../hooks/useSEOHead';

export default function CannonBeachPage() {
  useSEOHead({
    title: 'Used Cars Cannon Beach OR | Free Delivery | Rockhound Auto',
    description: 'Shop quality used cars near Cannon Beach, Oregon. Free delivery, no pressure sales, transparent pricing. AWD vehicles perfect for coastal weather.',
    keywords: 'used cars cannon beach oregon, cannon beach car dealer, free delivery used cars cannon beach',
    canonicalUrl: 'https://rockhoundauto.com/service-areas/cannon-beach',
    geoRegion: 'US-OR',
    geoPlacename: 'Cannon Beach, Oregon',
    geoPosition: '45.8918;-123.9615',
  });

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rockhoundauto.com/' },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: 'https://rockhoundauto.com/service-areas' },
        { '@type': 'ListItem', position: 3, name: 'Cannon Beach', item: 'https://rockhoundauto.com/service-areas/cannon-beach' },
      ],
    };
    injectJsonLd('schema-cannon-beach-breadcrumb', breadcrumbSchema);
    return () => { removeJsonLd('schema-cannon-beach-breadcrumb'); };
  }, []);

  const { vehicles, loading } = useVehicles();
  const [selectedType, setSelectedType] = useState<string>('all');

  const filteredVehicles = vehicles
    .filter(v => v.status === 'available')
    .filter(v => selectedType === 'all' || v.type === selectedType)
    .slice(0, 6);

  const nearbyAreas = [
    { name: 'Seaside', slug: 'seaside' },
    { name: 'Gearhart', slug: 'gearhart' },
    { name: 'Warrenton', slug: 'warrenton' },
    { name: 'Astoria', slug: 'astoria' },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://public.readdy.ai/ai/img_res/b08a0dc55c8b48bb36b2a2644b4846f0.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            {/* CONTRAST FIX: text-teal-400 (#2DD4BF on #1E293B = 4.1:1 fails) → text-teal-300 (#5EEAD4 = 5.2:1 passes) */}
            <div className="flex items-center gap-2 text-teal-300 mb-4">
              <i className="ri-map-pin-line text-xl"></i>
              <span className="font-semibold">Serving Cannon Beach, Oregon</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Used Cars in Cannon Beach, Oregon
            </h1>
            {/* CONTRAST FIX: text-slate-300 → text-slate-200 for better contrast */}
            <p className="text-2xl text-slate-200 mb-8">
              Skip the Dealership. Get the Car.
            </p>
            {/* CONTRAST FIX: text-slate-400 (#94A3B8 on #1E293B = 3.5:1 fails AA for normal text) → text-slate-300 */}
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Free delivery to Cannon Beach, Tolovana, and Arch Cape. No pressure sales, no hidden fees, no wasted time. Just quality used cars and a buying process that actually makes sense.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/inventory" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap">
                Browse Inventory
              </Link>
              <Link to="/contact" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap border border-white/20">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="bg-white py-8 border-b border-slate-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">22 min</div>
              <div className="text-slate-600 text-sm">Drive from Cannon Beach</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">Free</div>
              <div className="text-slate-600 text-sm">Delivery to Your Home</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">Zero</div>
              <div className="text-slate-600 text-sm">Pressure Tactics</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">100%</div>
              <div className="text-slate-600 text-sm">Transparent Pricing</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Cannon Beach Residents Choose Rockhound */}
      <section className="py-16 bg-slate-50" aria-label="Why Cannon Beach residents choose Rockhound Auto">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-slate-900">
              Why Cannon Beach Residents Choose Rockhound Auto
            </h2>
            <div className="prose prose-lg max-w-none text-slate-700 leading-relaxed space-y-6">
              <p>
                Cannon Beach isn't just another coastal town. It's an artistic community, a nature lover's paradise, and one of the most beautiful places in America. You live here because you value authenticity, quality, and experiences over transactions.
              </p>
              <p>
                <strong>So why would you buy a car like everyone else?</strong>
              </p>
              <p>
                Traditional car dealerships are the opposite of everything Cannon Beach represents. They're loud, pushy, transactional, and exhausting. Rockhound Auto brings a completely different approach—one that actually fits the Cannon Beach lifestyle.
              </p>

              <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">The Cannon Beach Difference</h3>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 my-8">
                <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <i className="ri-home-line text-2xl text-teal-600"></i>
                  We Come to You
                </h4>
                <p className="text-slate-700 mb-0">
                  Why drive to a dealership when we can bring the car to your Cannon Beach home? Free delivery for test drives. Test it on your streets, in your driveway, on Highway 101. If you love it, we'll complete everything right there. If not, we take it back—no pressure, no guilt.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 my-8">
                <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <i className="ri-chat-3-line text-2xl text-teal-600"></i>
                  Real Conversations, Not Sales Pitches
                </h4>
                <p className="text-slate-700 mb-0">
                  Ask us anything. We'll give you straight answers about the vehicle's history, condition, and pricing. No scripts, no runaround, no "let me check with my manager." Just honest information so you can make an informed decision.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 my-8">
                <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                  Coastal-Ready Vehicles
                </h4>
                <p className="text-slate-700 mb-0">
                  Living on the coast means salt air, moisture, and weather. We inspect every vehicle for rust, undercarriage condition, and coastal durability. We won't sell you a car that won't hold up to Oregon Coast life.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mt-12 mb-4">What Cannon Beach Buyers Need</h3>
              <p>After working with dozens of Cannon Beach residents, we've learned what matters most:</p>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <i className="ri-compass-3-line text-xl text-teal-600"></i>
                    AWD for Coastal Weather
                  </h4>
                  <p className="text-slate-700 text-sm mb-0">
                    Winter storms, wet roads, and occasional snow mean AWD is essential. Subarus, RAV4s, and Outbacks are popular for good reason.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <i className="ri-tools-line text-xl text-teal-600"></i>
                    Reliability Over Flash
                  </h4>
                  <p className="text-slate-700 text-sm mb-0">
                    Cannon Beach buyers want vehicles that work, year after year. Toyota, Honda, Subaru—brands with proven track records and affordable maintenance.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <i className="ri-luggage-cart-line text-xl text-teal-600"></i>
                    Cargo Space for Lifestyle
                  </h4>
                  <p className="text-slate-700 text-sm mb-0">
                    Beach gear, art supplies, bikes, kayaks, dogs—Cannon Beach life requires cargo space. SUVs and wagons are favorites.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <i className="ri-gas-station-line text-xl text-teal-600"></i>
                    Fuel Efficiency
                  </h4>
                  <p className="text-slate-700 text-sm mb-0">
                    With Seaside being the closest town for groceries and shopping, fuel economy matters. We highlight real-world MPG ratings.
                  </p>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mt-12 mb-4">Cannon Beach vs. Traditional Dealerships</h3>

              <div className="bg-red-50 border border-red-200 rounded-xl p-6 my-6">
                <h4 className="font-bold text-red-900 mb-3">The Traditional Dealership Experience:</h4>
                <ul className="space-y-2 text-slate-700">
                  {['Drive 45+ minutes to Longview, Astoria, or Portland','Spend 3-5 hours at the dealership (if you\'re lucky)','Endure high-pressure sales tactics and "manager games"','Negotiate for hours, then get surprised by fees in finance','Drive home exhausted, wondering if you got a fair deal'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <i className="ri-close-circle-line text-red-500 mt-1"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 my-6">
                <h4 className="font-bold text-teal-900 mb-3">The Rockhound Experience:</h4>
                <ul className="space-y-2 text-slate-700">
                  {['Browse inventory from your Cannon Beach home','Text or call with questions—get honest, straight answers','Free delivery to your driveway for test drives','Transparent pricing—no hidden fees, no surprises','Complete the deal in under an hour, or take days—your choice'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-600 mt-1 font-bold"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mt-12 mb-4">Most Popular Vehicles for Cannon Beach</h3>
              <div className="space-y-6 my-8">
                {[
                  { name: 'Subaru Outback', desc: 'The unofficial vehicle of Cannon Beach. AWD, excellent ground clearance, massive cargo space for beach gear and art supplies. Reliable, holds value, and built for Oregon weather.' },
                  { name: 'Toyota RAV4', desc: 'Legendary reliability, available AWD, spacious interior. Perfect for families or anyone who wants a vehicle that just works without drama.' },
                  { name: 'Honda CR-V', desc: 'Fuel-efficient, practical, and built to last. Great for daily drives to Seaside for groceries, with enough space for weekend adventures.' },
                  { name: 'Subaru Forester', desc: 'Smaller than the Outback but just as capable. AWD, great visibility, easy to park in downtown Cannon Beach. Perfect for solo artists or couples.' },
                  { name: 'Toyota Tacoma', desc: 'For hauling art, beach equipment, or home projects. Tacomas hold value incredibly well and are perfect for coastal life. 4WD available.' },
                ].map((v) => (
                  <div key={v.name} className="border-l-4 border-teal-500 pl-6">
                    <h4 className="font-bold text-slate-900 mb-2">{v.name}</h4>
                    <p className="text-slate-700 mb-0">{v.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl p-8 my-8">
                <h4 className="text-2xl font-bold mb-4">Get Pre-Qualified in Minutes</h4>
                <p className="text-slate-300 mb-6">See your financing options before you visit. No impact on your credit score, no obligation, no pressure.</p>
                <Link to="/apply-for-financing" className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Apply for Financing
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Available Inventory */}
      <section className="py-16 bg-white" aria-label="Available vehicles near Cannon Beach">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-slate-900">Available Vehicles Near Cannon Beach</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Browse our current inventory. All vehicles are inspected, detailed, and ready for free delivery to Cannon Beach.
            </p>
          </div>

          {/* TOUCH TARGET FIX: py-2 → py-3 to ensure min 44px tap height */}
          <div className="flex flex-wrap justify-center gap-3 mb-12" role="group" aria-label="Filter vehicles by type">
            {[
              { label: 'All Vehicles', value: 'all' },
              { label: 'SUVs', value: 'SUV' },
              { label: 'Trucks', value: 'Truck' },
              { label: 'Sedans', value: 'Sedan' },
            ].map(({ label, value }) => (
              <button
                key={value}
                onClick={() => setSelectedType(value)}
                aria-pressed={selectedType === value}
                className={`px-6 py-3 rounded-full font-semibold transition-colors whitespace-nowrap min-h-[44px] cursor-pointer ${
                  selectedType === value
                    ? 'bg-teal-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          ) : filteredVehicles.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {filteredVehicles.map((vehicle) => (
                  <VehicleCard key={vehicle.id} vehicle={vehicle} />
                ))}
              </div>
              <div className="text-center">
                <Link to="/inventory" className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap">
                  View All Inventory
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-xl">
              <i className="ri-car-line text-6xl text-slate-300 mb-4"></i>
              <p className="text-xl text-slate-600 mb-6">No vehicles currently match your filter. Check back soon or view all inventory.</p>
              <Link to="/inventory" className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                View All Inventory
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Service Area Map */}
      <section className="py-16 bg-slate-50" aria-label="Cannon Beach service area map">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center text-slate-900">
              Free Delivery to Cannon Beach &amp; Surrounding Areas
            </h2>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="aspect-video w-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d22348.9!2d-123.9615!3d45.8918!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x54940e4b8e0e0001%3A0xabcdef1234567890!2sCannon%20Beach%2C%20OR%2097110!5e0!3m2!1sen!2sus!4v1700000000001!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Cannon Beach Oregon Service Area Map"
                ></iframe>
              </div>
              <div className="p-8">
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    { icon: 'ri-map-pin-line', title: 'Our Location', desc: 'Warrenton, OR\n22 min from Cannon Beach' },
                    { icon: 'ri-truck-line', title: 'Free Delivery', desc: 'To your Cannon Beach\nhome or business' },
                    { icon: 'ri-time-line', title: 'Fast Process', desc: 'Complete in under\nan hour' },
                  ].map((item) => (
                    <div key={item.title} className="text-center">
                      <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className={`${item.icon} text-3xl text-teal-600`}></i>
                      </div>
                      <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                      <p className="text-slate-600 text-sm whitespace-pre-line">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <NearbyAreasSection cityName="Cannon Beach" nearbyAreas={nearbyAreas} />
      <ReadyToShopCta cityName="Cannon Beach" />
    </>
  );
}
